import { ErrorHandler, Injectable } from '@angular/core';
import { logError } from '@office-start/telemetry-service';
import { KnownErrorInstrumentationEvents } from '@office-start/telemetry-types';

import { InstrumentationParam, InstrumentationParams, InstrumentationService, KnownInstrumentationEvents } from '../shared/instrumentation/instrumentation.service';
import { ResultsWithCacheObservableError } from '../shared/models/operation-stream.model';
import { WindowService } from '../shared/services/window.service';
import { ErrorId } from './error-id';

const enum ErrorCategories {
    Angular = 'Angular2',
    Global = 'Global'
}

export class ApplicationError extends Error {
    constructor(
        public message: string,
        public originalError?: any
    ) {
        super(message);
        this.name = 'ApplicationError';
        Object.setPrototypeOf && Object.setPrototypeOf(this, ApplicationError.prototype);
    }
}

// The error handler class for all errors in Angular 2.
@Injectable({
    providedIn: 'root'
})
export class OfficeHomePageErrorHandler extends ErrorHandler {

    private readonly loggedErrors = new Map<string, number>();
    private readonly unknownError = 'UnknownError';

    // Ignore errors that are out of our control
    private readonly ignoredErrorSubstrings = [
        '__gCrWeb' // https://stackoverflow.com/questions/26483541/referenceerror-cant-find-variable-gcrweb
    ];

    constructor(
        private readonly instrumentationService: InstrumentationService,
        private readonly windowService: WindowService
    ) {
        super();
        this.mutateGlobalWindowErrorHandler();
    }

    /**
     * Handles global errors and either swallows or logs them
     *
     * @param error The error that was thrown
     * @param errorId Auto-generated ErrorId for this error
     *
     * @deprecated This function should no longer be used. Specific errors should be logged via
     * the `@office-start/telemetry-service`'s logError function.
     */
    public handleError(error: any, errorId?: ErrorId) {
        if (!this.shouldSwallow(error)) {
            this.setErrorId(error, errorId);
            this.logError(error, ErrorCategories.Angular);
        }
    }

    /**
     * Handle error returned for Observable which has both cache and network data is a wrapper around another error(s)
     * and ensure they are properly logged
     *
     * @param error The cached observable error to handle
     * @param cacheErrorId Auto-generated ErrorId from the cache
     * @param networkErrorId Auto-generated ErrorId for the network
     *
     * @deprecated This function should no longer be used. Specific errors should be logged via
     * the `@office-start/telemetry-service`'s logError function.
     */
    public handleCacheObservableError(error: ResultsWithCacheObservableError, cacheErrorId?: ErrorId, networkErrorId?: ErrorId) {
        if (error) {
            if (error.networkError) {
                this.handleError(error.networkError, networkErrorId);
            }
            if (error.cacheError) {
                this.handleError(error.cacheError, cacheErrorId);
            }
        }
    }

    private setErrorId(error: any, errorId?: ErrorId) {
        if (error && typeof (error) === 'object' && errorId) {
            error.id = errorId;
        }
    }

    private mutateGlobalWindowErrorHandler() {
        const _window = this.windowService.nativeWindow;
        const origErrorHandler = _window.onerror;
        const self = this;
        _window.onerror = function (message: string | ErrorEvent, filename?: string, lineno?: number, colno?: number, error?: Error) {
            let errorToLog: any = null;
            // Modern browsers pass message as ErrorEvent so prefer to use it for logging; otherwise use passed error object.
            // If error is not available then construct error based on message.
            if (typeof message === 'object' && message.type && message.type.toLowerCase() === 'error') {
                errorToLog = message;
            } else if (error) {
                errorToLog = error;
                errorToLog.message = error.message || message;
                Object.assign && Object.assign(errorToLog, {
                    filename,
                    lineno,
                    colno
                });
            } else if (typeof message === 'string') {
                errorToLog = new Error(message || self.unknownError);
                Object.assign && Object.assign(errorToLog, {
                    filename,
                    lineno,
                    colno
                });
            } else {
                errorToLog = new Error(self.unknownError);
                Object.assign && Object.assign(errorToLog, {
                    filename,
                    lineno,
                    colno
                });
            }

            self.logError(errorToLog, ErrorCategories.Global);

            // Call the original onerror handler on set of arguments that was passed to the override
            if (origErrorHandler) {
                try {
                    origErrorHandler.apply(_window, arguments);
                } catch (e) {
                    self.handleError(e);
                }
            }
        };
    }

    private logError(
        error: any,
        category: ErrorCategories) {
        // Errors can be reported in variety of ways on different browsers. In addition to category we will compose two
        // additional string values that will be logged as Error and DebugInfo. Note that Error will be logged in clear
        // text and DebugInfo will be logged as PiiString so take special care with data that goes into Error log property.
        let errorInfo = '';
        let debugInfo = '';

        error = error || new Error(this.unknownError);

        try {
            // Extract filename and location of the error in the script
            let filename: string = error.filename;
            if (filename) {
                // PII data maybe present in file path so ensure to keep only file name
                filename = filename.replace(/^.*[\\\/]/, '');
                errorInfo = `Error location: ${filename}, line: ${error.lineno}, col: ${error.colno}`;
            } else if (error.id) {
                // Identifiers can be found in error-id.ts and used to search for occurrence of error
                errorInfo = `Error id: ${error.id}`;
            } else {
                errorInfo = 'Error location: unknown';
            }

            // Determine error object containing original error. Modern browsers pass ErrorEvent object which contains error
            // object in separate property
            let errorObject = error.error || error;

            // Sometimes passed error can be a wrapper around another error
            if (errorObject.originalError) {
                errorInfo += `\nHandled error is a wrapper with message: ${errorObject.message}`;
                debugInfo = `Wrapper call stack: ${errorObject.stack || 'Call stack trace is not available'}`;
                errorObject = errorObject.originalError;
                debugInfo += `\nOriginal call stack: ${errorObject.stack || 'Call stack trace is not available'}`;
            } else {
                debugInfo = `Call stack: ${errorObject.stack || 'Call stack trace is not available'}`;
            }

            // Append error message to logged error info
            if (errorObject.message) {
                errorInfo += `\nError message: ${errorObject.message}`;
                // TODO: Remove after finish debugging IE 11 issue where "Invalid calling object" gets thrown when Shell deployments happen
                if (errorInfo.indexOf('Invalid calling object') > 0) {
                    errorInfo += `\nShellver: ${(window as any).O365ShellVerStr}`;
                }
            }
        } catch (e) {
            errorInfo = `Error caught inside error logger: ${(e && e.message) || this.unknownError}`;
            debugInfo = `Call stack: ${(e && e.stack) || 'Call stack trace is not available'}`;
            category = ErrorCategories.Angular;

            // Delegate to the default handler but bypass our implementation of handleError() to prevent infinite loop
            super.handleError(e);
        } finally {
            this.safeLogErrorData(errorInfo, debugInfo, category);

            // Allow default Angular handler to get crack at the original error
            super.handleError(error);
        }
    }

    private safeLogErrorData(
        errorInfo: string,
        debugInfo: string,
        category: ErrorCategories) {
        // Log error properties; if this code fails then abandon logging
        try {
            // Keep track of all logged info to prevent over-logging of exceptions that happen in cycle.
            // This is done to ensure that we do not overwhelm system with exceptions that happen frequently.
            // Basically what happens here is that we reduce the number of logged events that are instances of the same exception.
            // All occurrences from 1-9 are logged individually; from 10-100 only when we hit occurrence 10, 20, 30, 40, ... 90;
            // once we hit 100s we will only log 100, 200, ... 900 occurrence; anything beyond 1000 will get logged on 1000, 2000, 3000, ... 9000
            // and so on and so forth for next power of 10.
            const occurrence = this.determineOccurrence(errorInfo);
            this.loggedErrors.set(errorInfo, occurrence);

            if (this.shouldLogBasedOnOccurrence(occurrence)) {
                const params: InstrumentationParams = {
                    [InstrumentationParam.ItemCategory]: category,
                    [InstrumentationParam.ItemCount]: occurrence.toString()
                };
                this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.UnhandledException, params);

                // Exceptional telemetry tends to be large and has a limited useful lifetime. Additionally
                // it may contain unexpected values that sometimes exceed the allowed System Metadata
                // or EUPI classifications for our telemetry -- resulting in a potential privacy event.

                // Good practice, if such telemetry is needed, is to ensure it is placed into a storage location with
                // limited retention, such as the generic_debug_cache_2 which has a maximum retention of 48 hours.

                // Best practice is to not log it at all. If it must be logged, then log a well known sentinel value
                // or enumerated value instead that represents the type of exception or issue encountered.

                // Large, limited lifetime, or potentially privacy impacting telemetry should NEVER be logged
                // into any long term data store such as COSMOS.
                params[InstrumentationParam.Error] = errorInfo;
                params[InstrumentationParam.DebugInfo] = debugInfo;
                this.instrumentationService.logDebugCacheEvent(KnownInstrumentationEvents.UnhandledException, params);

                // TODO: Remove above aria logging after OTEL migration
                logError({ eventName: KnownErrorInstrumentationEvents.UnhandledException, errorType: 'Unhandled_Exception', message: errorInfo, debugInfo});
            }
        } catch (e) {
            // Delegate to the default handler but bypass our implementation of handleError() to prevent infinite loop
            super.handleError(e);
        }
    }

    private shouldSwallow(error: any): boolean {
        // If this is an AccessTokenResponse error it will have a property specifying if the error should be swallowed.
        if (error?.shouldSwallow) {
            return true;
        }

        const errorMessage = (error?.error || error)?.message;
        if (errorMessage) {
            for (const i in this.ignoredErrorSubstrings) {
                if (errorMessage.indexOf(i) >= 0) {
                    return true;
                }
            }
        }

        // If handler called explicitly then allow error representing code failure to pass through.
        // Use presence of 'stack' property as a proxy for code error unless it is error from promise rejection which is handled below.
        if (error && error.stack && !error.promise) {
            return false;
        }

        // Rejection from promises that do not have explicit .catch() and error callback will be wrapped and passed through to this error handler.
        // We want to swallow the errors that simply indicate that promise was rejected. This can happen in variety of ways because reject accepts
        // 'any' as parameter. So assumption that we make here is that if object passed to rejection does not have 'stack' property then it is
        // safe to swallow.
        // However there is significant number of cases where rejection id done by doing 'throw new Error()'. We are seeing it in Angular codebase
        // especially in conjunction with async/ await pattern. When that happens generated error will have its rejection property set to Error
        // with stack pointing to the place where error was thrown. In order to swallow that kind of rejection we make an assertion that we will
        // swallow errors that have error.name === 'Error' (i.e. objects instantiated using new Error()). It is safe assumption because the errors
        // that we want to log are code errors and those tend to be specialized: TypeError, SyntaxError, etc.
        if (error && error.promise && error.rejection) {
            const rejection = error.rejection;
            if (rejection.stack && rejection.name !== 'Error') {
                return false;
            }
        }

        return true;
    }

    private determineOccurrence(errorMessage: string): number {
        let occurrence = 1;

        if (this.loggedErrors.has(errorMessage)) {
            occurrence = this.loggedErrors.get(errorMessage) + 1;
        }

        return occurrence;
    }

    private shouldLogBasedOnOccurrence(occurrence: number): boolean {
        const occurrenceLength = occurrence.toString().length;
        const threshold = Math.pow(10, occurrenceLength - 1);
        return threshold < 2 ? true : occurrence % threshold === 0;
    }
}
